<script setup>
import { computed, onMounted, ref } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"

import store from "../../../state/store"
import useSellerProducts from "../../../use/useSellerProducts"
import useDebouncedFunction from "../../../use/useDebouncedFunction"

const products = computed(() => store.state.products)

const { productStatus, search, perPage, storeGetProducts } = useSellerProducts()
const { handleInput } = useDebouncedFunction(storeGetProducts)

const formattedPrices = computed(() => {
  return products.value.data.map((product) =>
    new Intl.NumberFormat("fa-IR").format(product.price)
  )
})

const formattedDiscount = computed(() => {
  return products.value.data.map((product) =>
    new Intl.NumberFormat("fa-IR").format(product.discount)
  )
})

onMounted(() => {
  storeGetProducts()
})

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  storeGetProducts(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی نام محصول ..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="storeGetProducts(null)"
              v-model="productStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت محصول</option>
              <option value="0">منتشر نشده</option>
              <option value="1">منتشر شده</option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="storeGetProducts(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="productTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="product_image">تصویر</th>
              <th class="sort" data-sort="product_name">نام محصول</th>
              <th class="sort" data-sort="category_name">نام دسته بندی</th>
              <th class="sort" data-sort="product_price">قیمت</th>
              <th class="sort" data-sort="product_discount">قیمت با تخفیف</th>
              <th class="sort" data-sort="product_inventory">موجودی</th>
              <th class="sort" data-sort="status">وضعیت انتشار</th>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody class="list" v-if="products.loading || !products.data.length">
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="products.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ محصولی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(product, index) of products.data" :key="index">
              <td class="product_image">
                <div class="avatar-sm bg-light rounded p-1">
                  <img
                    :src="product.image"
                    alt="product.name"
                    class="img-fluid d-block"
                  />
                </div>
              </td>
              <td class="product_name">{{ product.name }}</td>

              <td class="category_name">{{ product.category_name }}</td>
              <td class="product_price">{{ formattedPrices[index] }}</td>
              <td class="product_discount">
                {{ product.discount !== null ? formattedDiscount[index] : "-" }}
              </td>
              <td class="product_inventory">{{ product.inventory }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': product.status == 1,
                    'badge-soft-danger': product.status == 0,
                  }"
                  >{{ product.status ? "منتشر شده" : "منتشر نشده" }}</span
                >
              </td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <RouterLink
                      :to="{
                        name: 'store.product.edit',
                        params: { id: product.id },
                      }"
                      class="text-primary d-inline-block edit-item-btn"
                      ><i class="ri-pencil-fill fs-16"></i
                    ></RouterLink>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Remove"
                  >
                    <b-link
                      class="text-danger d-inline-block remove-item-btn"
                      @click="deletedata(item)"
                    >
                      <i class="ri-delete-bin-5-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="products.total > products.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of products.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
